import React, { useEffect, useState } from "react";
import {
  Text,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
  Tag,
  Spinner,
  tokens,
  Body1,
  mergeClasses,
  teamsDarkTheme,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Radio,
  RadioGroup,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  ArrowRight24Regular,
  Warning24Regular,
  Play24Regular,
  ChevronDownRegular,
} from "@fluentui/react-icons";
import { DocumentClassification, DocumentPrompts } from "@/types/risk-analysis";
import { BUTTON_WIDTH, TH_PADDING, useRiskAnalysisTableStyles } from "./styles";
import { trackEvent } from "@/services/analytics-service";
import { AnalyticsKey } from "@/types/analytics";
import { useAuth } from "@/context/auth";
import { stoplightColorMapper } from "@/components/label-accordion/styles";
import { searchAndRemoveHighlight } from "@/taskpane/lib";
import { useDocumentClassification } from "@/context/document-classification";
import { DOCUMENT_STORAGE_KEY, saveToDocumentStorage } from "@/taskpane/lib/common/document-storage";
import { useTheme } from "@/context/theme";
import { DocumentParagraph } from "@/types/document";
import { WarningDialog } from "./WarningDialog";
import { PromptsDialog } from "./PromptsDialog";
import { RiskAnalysisDialog } from "./RiskAnalysisDialog";
import { RedlinesProvider } from "@/context/redlines";

interface RiskAnalysisTableProps {
  classification: DocumentClassification;
  handleHighlight: (paragraph: DocumentParagraph) => Promise<void>;
  promptsUsed?: DocumentPrompts;
  isLoadingPlaybook?: boolean;
  refreshAnalysis: (playbookRuleId?: DocumentClassification["rules"][number]["id"]) => void;
}

export const RiskAnalysisTable: React.FC<RiskAnalysisTableProps> = ({
  classification,
  handleHighlight,
  promptsUsed,
  isLoadingPlaybook = false,
  refreshAnalysis,
}) => {
  const { theme } = useTheme();
  const isDarkTheme = theme === teamsDarkTheme;
  const styles = useRiskAnalysisTableStyles();
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);
  const [currentIndices, setCurrentIndices] = useState<Record<string, number>>({});
  const [isRiskAnalysisDialogOpen, setIsRiskAnalysisDialogOpen] = useState(false);
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [isPromptsDialogOpen, setIsPromptsDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<DocumentClassification["rules"][number] | null>(null);
  const [selectedPromptRule, setSelectedPromptRule] = useState<Omit<
    DocumentPrompts["rules"][number],
    "id" | "clauseTopic"
  > | null>(null);
  const { setClassifications } = useDocumentClassification();

  const { user } = useAuth();
  const isAdminUser = user?.user?.role === "admin";

  const handleNavigate = (data: DocumentClassification["rules"][number], direction: "prev" | "next") => {
    const currentIndex = currentIndices[data.clauseTopic] ?? -1;
    let newIndex: number;

    if (direction === "prev") {
      newIndex = currentIndex > 0 ? currentIndex - 1 : data.paragraphs.length - 1;
      trackEvent(AnalyticsKey.USER.RISK_ANALYSIS.LABEL_ACCORDION_RESULT.HIGHLIGHT_NAVIGATION.PREVIOUS, {
        userId: user.user.id,
        label: data.clauseTopic,
        highlightIndex: newIndex,
        highlightCount: data.paragraphs.length,
      });
    } else {
      newIndex = currentIndex < data.paragraphs.length - 1 ? currentIndex + 1 : 0;
      trackEvent(AnalyticsKey.USER.RISK_ANALYSIS.LABEL_ACCORDION_RESULT.HIGHLIGHT_NAVIGATION.NEXT, {
        userId: user.user.id,
        label: data.clauseTopic,
        highlightIndex: newIndex,
        highlightCount: data.paragraphs.length,
      });
    }

    newIndex = Math.max(0, Math.min(newIndex, data.paragraphs.length - 1));
    setCurrentIndices({ ...currentIndices, [data.clauseTopic]: newIndex });
    handleHighlight(data.paragraphs?.[newIndex]);
  };

  const handleViewFullReport = (data: DocumentClassification["rules"][number]) => {
    trackEvent(AnalyticsKey.USER.RISK_ANALYSIS.LABEL_ACCORDION_RESULT.VIEW_FULL_REPORT, {
      userId: user.user.id,
      label: data.clauseTopic,
      fullReport: JSON.stringify(data?.analysis),
    });
    setClassifications((prev) => {
      const viewedClassification = prev.map((classification) => ({
        ...classification,
        rules: classification.rules.map((rule) => (rule.id === data.id ? { ...rule, viewed: true } : rule)),
      }));
      saveToDocumentStorage(DOCUMENT_STORAGE_KEY.CLASSIFICATIONS, JSON.stringify(viewedClassification));
      return viewedClassification;
    });
    setSelectedRule(data);
    setIsRiskAnalysisDialogOpen(true);
  };

  const handleViewPrompts = (data: DocumentClassification["rules"][number]) => {
    setSelectedRule(data);
    setSelectedPromptRule({
      ...promptsUsed?.rules.find((rule) => rule.id === data.id),
    });
    setIsPromptsDialogOpen(true);
  };

  const handleExpandToggle = (data: DocumentClassification["rules"][number]) => {
    const term = data.clauseTopic;
    if (expandedTerm === term) {
      setExpandedTerm(null);
      setCurrentIndices((prev) => ({ ...prev, [term]: 0 }));
      searchAndRemoveHighlight(data.paragraphs?.[0]);
    } else {
      setCurrentIndices((prev) => ({ ...prev, [term]: 0 }));
      handleHighlight(data.paragraphs?.[0]);
      setExpandedTerm(term);
    }
  };

  const handleViewWarnings = (data: DocumentClassification["rules"][number]) => {
    setSelectedRule(data);
    setSelectedPromptRule({
      ...promptsUsed?.rules.find((rule) => rule.id === data.id),
    });
    setIsWarningDialogOpen((prev) => !prev);
  };

  const getValueExtractionText = (data: DocumentClassification["rules"][number]) => {
    if (!data.paragraphs?.length) return "Not Included";
    return data?.analysis?.clause?.resultStatus?.message || "";
  };

  useEffect(() => {
    if (!selectedRule) return;

    setCurrentIndices((prev) => ({ ...prev, [selectedRule.clauseTopic]: 0 }));

    if (isRiskAnalysisDialogOpen) {
      handleHighlight(selectedRule?.paragraphs?.[0]);
    } else {
      searchAndRemoveHighlight(selectedRule?.paragraphs?.[0]);
    }
  }, [isRiskAnalysisDialogOpen, selectedRule]);

  return (
    <>
      <div className={styles.tableContainer}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>TERM</TableHeaderCell>
              {classification?.rules?.some((data) => data?.analysis || data.isLoading || data.isCanceled) && (
                <>
                  <TableHeaderCell>VALUE</TableHeaderCell>
                  <TableHeaderCell
                    style={{ width: `calc(${isAdminUser ? BUTTON_WIDTH * 2 : BUTTON_WIDTH}px + ${TH_PADDING}px)` }}
                  />
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {classification?.rules
              ?.sort((a, b) => a.order - b.order)
              .map((data) => (
                <React.Fragment key={data.id}>
                  <TableRow
                    style={{
                      backgroundColor: data.viewed
                        ? isDarkTheme
                          ? tokens.colorBrandBackgroundPressed
                          : "#add8e6"
                        : "unset",
                    }}
                  >
                    <TableCell className={data?.isCanceled ? styles.canceledCell : ""}>
                      <div className={styles.fallbackRow}>
                        <Text>{data.clauseTopic}</Text>
                        <FallbackRulesPopover
                          classificationRule={data}
                          disabled={isLoadingPlaybook || data.isLoading}
                        />
                      </div>
                    </TableCell>
                    {(data?.analysis || data.isLoading) && (
                      <TableCell>
                        <div className={styles.statusBadgeContainer}>
                          <Button
                            appearance="subtle"
                            size="small"
                            className={styles.statusBadge}
                            onClick={() => handleExpandToggle(data)}
                            disabled={isLoadingPlaybook || data.isLoading || !data.paragraphs?.length}
                            style={{
                              backgroundColor:
                                isLoadingPlaybook || data.isLoading
                                  ? tokens.colorTransparentBackground
                                  : stoplightColorMapper[data?.analysis?.clause?.resultStatus?.color?.toLowerCase()] ||
                                    stoplightColorMapper.unavailable,
                            }}
                          >
                            {isLoadingPlaybook || data.isLoading ? (
                              <Spinner className={styles.spinner} size="extra-tiny" label="Loading..." />
                            ) : (
                              getValueExtractionText(data)
                            )}
                          </Button>
                          {data?.validationErrors?.length > 0 && (
                            <Button
                              appearance="transparent"
                              onClick={() => handleViewWarnings(data)}
                              className={styles.warningBadgeContainer}
                            >
                              <Tag size="small" color="warning" className={styles.warningBadge}>
                                <Warning24Regular fontSize={12} />
                                <span>{data?.validationErrors?.length}</span>
                              </Tag>
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                    {data.isCanceled && (
                      <>
                        <TableCell className={mergeClasses(styles.canceledCell, styles.textCenter)}>
                          <Body1>Analysis stopped</Body1>
                        </TableCell>
                        <TableCell className={mergeClasses(styles.canceledCell, styles.textCenter)}>
                          <Button
                            size="small"
                            appearance="primary"
                            onClick={() => refreshAnalysis(data.id)}
                            icon={<Play24Regular />}
                            disabled={isLoadingPlaybook || data.isLoading}
                          />
                        </TableCell>
                      </>
                    )}
                    {(data?.analysis || data.isLoading) && (
                      <>
                        <TableCell className={styles.actionButtons}>
                          {user?.user?.role === "admin" && (
                            <Button
                              size="small"
                              appearance="primary"
                              onClick={() => handleViewPrompts(data)}
                              disabled={isLoadingPlaybook || data.isLoading}
                            >
                              Prompts
                            </Button>
                          )}
                          <Button
                            size="small"
                            appearance="primary"
                            onClick={() => handleViewFullReport(data)}
                            disabled={isLoadingPlaybook || data.isLoading}
                          >
                            Full Report
                          </Button>
                          <Button
                            size="small"
                            appearance="primary"
                            onClick={() => refreshAnalysis(data.id)}
                            icon={<Play24Regular />}
                            disabled={isLoadingPlaybook || data.isLoading}
                          />
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                  {expandedTerm === data.clauseTopic && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <div className={styles.summaryContainer}>
                          <div className={styles.navigationControls}>
                            <div className={styles.navigationButtons}>
                              <Button
                                disabled={!data.paragraphs?.length || isLoadingPlaybook || data.isLoading}
                                icon={<ArrowLeft24Regular />}
                                onClick={() => handleNavigate(data, "prev")}
                                aria-label="Previous"
                              />
                              <Button
                                disabled={!data.paragraphs?.length || isLoadingPlaybook || data.isLoading}
                                icon={<ArrowRight24Regular />}
                                onClick={() => handleNavigate(data, "next")}
                                aria-label="Next"
                              />
                              <span>
                                {Math.min(data.paragraphs?.length || 0, currentIndices[data.clauseTopic] ?? 0) + 1}/
                                {data.paragraphs?.length}
                              </span>
                            </div>
                          </div>
                          <h3>Summary:</h3>
                          <ul>{data?.analysis?.clause?.summary?.map((item, index) => <li key={index}>{item}</li>)}</ul>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
      </div>

      {selectedRule && (
        <>
          <RedlinesProvider classification={classification} selectedRule={selectedRule}>
            <RiskAnalysisDialog
              open={isRiskAnalysisDialogOpen}
              onOpenChange={setIsRiskAnalysisDialogOpen}
              selectedRule={selectedRule}
              isLoadingPlaybook={isLoadingPlaybook}
              currentIndex={currentIndices?.[selectedRule.clauseTopic] ?? 0}
              onNavigate={(direction) => handleNavigate(selectedRule, direction)}
              styles={styles}
              onRefreshAnalysis={() => setIsRiskAnalysisDialogOpen(false)}
            />
          </RedlinesProvider>

          <WarningDialog
            open={isWarningDialogOpen}
            onOpenChange={setIsWarningDialogOpen}
            validationErrors={selectedRule?.validationErrors}
            dialogTitleClassName={styles.dialogTitle}
          />
        </>
      )}
      {selectedPromptRule && selectedRule && (
        <PromptsDialog
          open={isPromptsDialogOpen}
          onOpenChange={setIsPromptsDialogOpen}
          selectedRule={selectedRule}
          selectedPromptRule={selectedPromptRule}
          dialogTitleClassName={styles.dialogTitle}
          promptContentClassName={styles.promptContent}
        />
      )}
    </>
  );
};

interface FallbackRulesPopoverProps {
  disabled?: boolean;
  classificationRule: DocumentClassification["rules"][number];
}

function FallbackRulesPopover({ disabled, classificationRule }: FallbackRulesPopoverProps) {
  const styles = useRiskAnalysisTableStyles();
  const { selectedPlaybook, setPlaybooks, setSelectedPlaybook } = useDocumentClassification();

  const handleSelectFallbackRule = (playbookRuleId: string, fallbackRuleId: string) => {
    setPlaybooks((prev) =>
      prev.map((playbook) => ({
        ...playbook,
        rules: playbook.rules.map((rule) =>
          rule.id === playbookRuleId ? { ...rule, selectedCustomerRuleId: fallbackRuleId } : rule
        ),
      }))
    );
    setSelectedPlaybook({
      ...selectedPlaybook,
      rules: selectedPlaybook.rules.map((rule) =>
        rule.id === playbookRuleId ? { ...rule, selectedCustomerRuleId: fallbackRuleId } : rule
      ),
    });
  };

  const playbookRule = selectedPlaybook.rules.find((rule) => rule.id === classificationRule.id);
  const customerRules = (playbookRule.customerRules ?? []).sort((a, b) => a.order - b.order);

  if (!customerRules.length) return null;
  return (
    <Popover trapFocus positioning={{ position: "below", offset: { mainAxis: 4 } }}>
      <PopoverTrigger disableButtonEnhancement>
        <Button size="small" disabled={disabled} icon={<ChevronDownRegular fontSize={12} />} />
      </PopoverTrigger>
      <PopoverSurface aria-labelledby={playbookRule.id} style={{ minWidth: 250 }}>
        <div className={styles.popoverTitle}>
          <Text>Customer rule options</Text>
        </div>
        <RadioGroup>
          {customerRules.map((fallback) => (
            <Radio
              key={fallback.id}
              value={fallback.id}
              label={fallback.title}
              checked={playbookRule.selectedCustomerRuleId === fallback.id}
              onChange={() => handleSelectFallbackRule(playbookRule.id, fallback.id)}
            />
          ))}
        </RadioGroup>
      </PopoverSurface>
    </Popover>
  );
}
